import React, { useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels } from 'wsm-common-data';
import { Panel } from 'react-bootstrap';
import { trackEvent } from 'ddc-track-event';
import { Dropdown } from 'wsm-dropdown';

import Facet from './Facet';
import FacetTitle from './FacetTitle';
import FacetGeoLocation from './geo-location/FacetGeoLocation';
import { transformUserRangeData } from '../utilities/facet-user-range';
import { FACET_OVERRIDE_TYPE_SELECT, priceFacetIds } from '../global-constants';
import FacetSkeleton from './FacetSkeleton';
import WebIntegrationPlaceholder from './WebIntegrationPlaceholder';

const FacetUserRange = React.lazy(() =>
	import(
		/* webpackChunkName: "FacetUserRange" , webpackPrefetch: true */ './user-range/FacetUserRange'
	)
);

const FacetGroup = ({
	facet,
	detailsLinkData,
	openDetailsModal,
	selectedValues,
	renderPanel = true,
	overrideType
}) => {
	const {
		type,
		label,
		id: groupId,
		multiselect = undefined,
		values,
		collapsed
	} = facet || {};
	const facetType = multiselect ? 'checkbox' : 'radio';
	const { widgetName, windowId } = useRequestData();
	const [isCollapsed, setCollapsed] = useState(collapsed);
	const labels = useLabels();
	const geoLocationLabel = labels.get('DISTANCE');
	let facetTitle = label;

	// State derived from getAppliedFilters that represents currently selected facets
	const [userSelections, setUserSelections] = useState(selectedValues);

	useEffect(() => {
		setUserSelections(selectedValues);
	}, [selectedValues]);

	const updateFacets = (facetId, targetValue, event = null) => {
		const newSelectedIds = [];

		if (facetType === 'checkbox') {
			selectedValues.map((selected) =>
				newSelectedIds.push(selected.value)
			);
			// Remove selected value from array if already included
			const selectedIndex = newSelectedIds.indexOf(targetValue);
			if (selectedIndex !== -1) {
				newSelectedIds.splice(selectedIndex, 1);
			} else {
				// Add facet id to list of selected ids
				newSelectedIds.push(targetValue);
			}
			// Don't push anything to updatedSelections when the selection is ALL
		} else if (targetValue !== 'ALL') {
			newSelectedIds.push(targetValue);
		}

		window.DDC.InvData.setFilterSelections({
			filterName: facetId,
			filterValue: targetValue,
			filterValues: newSelectedIds,
			isChip: false,
			isSelected: newSelectedIds.includes(targetValue),
			isSlider: false,
			element: event && event.target
		});

		setUserSelections(
			values.filter((selection) =>
				newSelectedIds.includes(selection.value)
			)
		);

		if (newSelectedIds.includes(targetValue)) {
			trackEvent(widgetName, windowId, {
				action: 'applied',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet added'
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'removed',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet removed'
			});
		}
	};

	const handleDropdownChange = (option) => {
		const { groupId: facetId, newValue: targetValue } = option;
		updateFacets(facetId, targetValue);
	};

	const handleChange = (event, facetId) => {
		const targetValue = event.target.value;
		updateFacets(facetId, targetValue, event);
	};

	/**
	 * Creates a list of Facet components for each FacetGroup
	 *
	 * @event handleChange
	 * @returns {Facet[] || FacetUserRange || null} Facet components
	 */

	const getFacets = () => {
		if (values?.length) {
			// TODO: Move this to configurable option in composer and facet-config
			if (overrideType && overrideType === FACET_OVERRIDE_TYPE_SELECT) {
				/*
					TODO:
					- add container element to dropdown module
				*/
				return (
					<div className="dropdown-container" key={groupId}>
						<Dropdown
							group={facet}
							onChange={(option) => handleDropdownChange(option)}
							groupTitle={facetTitle}
							inputClass="border w-100 dropdown-toggle btn font-weight-normal text-left text-neutral-1000 bg-neutral-0"
						/>
					</div>
				);
			}

			if (type === 'USER_RANGE') {
				const [facetValues, noPriceObj] = transformUserRangeData(
					facet,
					!!multiselect
				);
				if (facetValues) {
					return (
						<Suspense fallback={<FacetSkeleton />}>
							<FacetUserRange
								facetValues={facetValues}
								selectedValues={selectedValues}
								noPriceObj={noPriceObj}
								facetId={facet.id}
								isMultiselect={!!multiselect}
							/>
						</Suspense>
					);
				} else {
					return null;
				}
			}

			if (type === 'GEO') {
				facetTitle = geoLocationLabel;
				return <FacetGeoLocation />;
			}

			return (
				<ol className="list-unstyled line-height-condensed">
					{values?.map((item) => {
						// values = DDC.InvData.facets[this facet group].values
						const {
							label: facetLabel,
							value: facetValue,
							count: facetCount
						} = item;
						let selected = !!userSelections.find(
							(selection) => selection.value === facetValue
						);
						if (!userSelections.length && facetValue === 'ALL') {
							selected = true;
						}
						return (
							<Facet
								facetLabel={facetLabel}
								facetType={facetType}
								facetValue={facetValue}
								facetCount={facetCount}
								groupId={groupId}
								isSelected={selected}
								isDisabled={false}
								onChange={handleChange}
								key={`${groupId}-${facetValue}`}
							/>
						);
					})}
				</ol>
			);
		} else {
			return null;
		}
	};

	const [isInteracted, setInteracted] = useState(false);
	const handleCollapse = () => {
		setCollapsed(!isCollapsed);
		setInteracted(true);

		if (!isCollapsed) {
			trackEvent(widgetName, windowId, {
				action: 'collapsed',
				element: `${groupId} accordion`,
				result: 'Accordion collapsed'
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'expanded',
				element: `${groupId} accordion`,
				result: 'Accordion expanded'
			});
		}
	};
	const expandIcon = isCollapsed ? 'ddc-icon-expand' : 'ddc-icon-collapse';

	let facets = null;
	// Display request facets only when the panel is open
	// or user has previously toggled the panel
	// or if we're not rendering a panel
	if (!renderPanel || !isCollapsed || isInteracted) {
		facets = getFacets();

		if (!facets) {
			return null;
		}
	}

	if (!renderPanel) {
		return <React.Fragment>{facets}</React.Fragment>;
	}

	const wiapiPlaceholder = priceFacetIds.includes(groupId) ? (
		<WebIntegrationPlaceholder
			location="search-facet-pricing"
			locationId={groupId}
		/>
	) : null;

	const hasDetails = detailsLinkData?.details;

	return (
		<Panel
			className="facet-list-group"
			id={groupId}
			onToggle={handleCollapse}
			expanded={!isCollapsed}
			data-facet-group={groupId}
			role="tablist"
		>
			<Panel.Heading>
				<Panel.Title toggle>
					<FacetTitle {...{ facetTitle, expandIcon }} />
				</Panel.Title>
			</Panel.Heading>
			<Panel.Body collapsible>
				{facets}
				{wiapiPlaceholder}
				{hasDetails && (
					<button
						type="button"
						className="text-link text-decoration-underline text-link-muted text-left"
						onClick={() =>
							openDetailsModal(facetTitle, detailsLinkData)
						}
					>
						<span className="ddc-font-size-xsmall">
							{labels.get('ABOUT')} {facetTitle}
						</span>
					</button>
				)}
			</Panel.Body>
		</Panel>
	);
};

FacetGroup.propTypes = {
	facet: PropTypes.shape({
		type: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		isCurrency: PropTypes.bool,
		multiselect: PropTypes.bool,
		values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
		collapsed: PropTypes.bool.isRequired
	}).isRequired,
	detailsLinkData: PropTypes.shape({
		details: PropTypes.string,
		subText: PropTypes.string
	}),
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	renderPanel: PropTypes.bool,
	overrideType: PropTypes.string,
	openDetailsModal: PropTypes.func
};

export default FacetGroup;
